import {
    ConfigurationBody,
    EmissionsConfiguration,
    EmissionRecordsPage,
    EmissionRecordsQuery,
    EmissionRecordsResponse,
    EmissionsConfigurationSaveResponse,
    EmissionsTimelineResult,
    OutboundMessagingConfiguration,
    OutboundMessagingRequest,
} from "models/Emissions";
import { BaseService } from "./BaseService";

const includeInactiveParams = () =>
    new URLSearchParams({ includeInactive: "true" });

const emissionBodyParams = (
    name: string,
    description: string | null,
    extra?: Record<string, string>,
) => {
    const params = new URLSearchParams({ ...extra, name });
    if (description) params.set("description", description);
    return params;
};

export class EmissionsService extends BaseService {
    protected urlBase = "api";

    public getEndpoints(abortSignal?: AbortSignal) {
        return this.get<OutboundMessagingConfiguration[]>(
            includeInactiveParams(),
            "OutboundMessaging",
            abortSignal,
        );
    }

    public getEndpoint(id: string, abortSignal?: AbortSignal) {
        return this.get<OutboundMessagingConfiguration>(
            includeInactiveParams(),
            `OutboundMessaging/${id}`,
            abortSignal,
        );
    }

    public createEndpoint(request: OutboundMessagingRequest) {
        return this.post<OutboundMessagingConfiguration>(
            JSON.stringify(request),
            new URLSearchParams(),
            "OutboundMessaging",
        );
    }

    public updateEndpoint(id: string, request: OutboundMessagingRequest) {
        return this.put<OutboundMessagingConfiguration>(
            new URLSearchParams(),
            `OutboundMessaging/${id}`,
            JSON.stringify(request),
        );
    }

    /**
     * Status-only change. Update validates the whole configuration, which would stop a
     * configuration that no longer validates from being switched off at all.
     */
    public setEndpointStatus(id: string, isActive: boolean) {
        return this.patch<OutboundMessagingConfiguration>(
            JSON.stringify({ isActive }),
            new URLSearchParams(),
            `OutboundMessaging/${id}/status`,
        );
    }

    public getEmissionBodies(abortSignal?: AbortSignal) {
        return this.get<EmissionsConfiguration[]>(
            includeInactiveParams(),
            "EmissionsConfiguration",
            abortSignal,
        );
    }

    public getEmissionBody(id: string, abortSignal?: AbortSignal) {
        return this.get<EmissionsConfiguration>(
            includeInactiveParams(),
            `EmissionsConfiguration/${id}`,
            abortSignal,
        );
    }

    public createEmissionBody(
        name: string,
        description: string | null,
        configuration: ConfigurationBody,
    ) {
        return this.post<EmissionsConfigurationSaveResponse>(
            JSON.stringify(configuration),
            emissionBodyParams(name, description),
            "EmissionsConfiguration",
        );
    }

    public updateEmissionBody(
        id: string,
        name: string,
        description: string | null,
        configuration: ConfigurationBody,
        isActive?: boolean,
    ) {
        const extra: Record<string, string> = { id };
        if (isActive !== undefined) extra.isActive = String(isActive);

        return this.put<EmissionsConfigurationSaveResponse>(
            emissionBodyParams(name, description, extra),
            "EmissionsConfiguration",
            JSON.stringify(configuration),
        );
    }

    /** Status-only change; see setEndpointStatus. Enabling still validates server side. */
    public setEmissionBodyStatus(id: string, isActive: boolean) {
        return this.patch<EmissionsConfiguration>(
            JSON.stringify({ isActive }),
            new URLSearchParams(),
            `EmissionsConfiguration/${id}/status`,
        );
    }

    public getEmissionsTimeline(amdId: string, abortSignal?: AbortSignal) {
        return this.get<EmissionsTimelineResult>(
            new URLSearchParams({ amdId }),
            "pipeline/EmissionsTimeline/GetEmissionsTimelineByAudioMetadataId",
            abortSignal,
        );
    }

    // The endpoint pages by continuation token rather than offset, so a count would mean reading every
    // record. Each page's token is kept, which makes the next page one request and only a jump ahead
    // walks the pages in between. The count is therefore a lower bound until the last page
    private readonly recordPageTokens = new Map<string, string[]>();

    // Cursors describe a list as it was when the walk started, so a reload has to drop them
    // rather than resume a chain whose rows have since shifted.
    public clearEmissionRecordCursors() {
        this.recordPageTokens.clear();
    }

    public async getEmissionRecords(
        {
            page,
            pageSize,
            conversationId,
            recordType,
            configurationId,
            from,
        }: EmissionRecordsQuery,
        abortSignal?: AbortSignal,
    ): Promise<EmissionRecordsPage> {
        const filters = { conversationId, recordType, configurationId, from };
        const key = JSON.stringify({ ...filters, pageSize });
        const tokens = this.recordPageTokens.get(key) ?? [""];
        this.recordPageTokens.set(key, tokens);

        const fetchPage = (continuationToken: string) => {
            const params = new URLSearchParams({ pageSize: String(pageSize) });
            Object.entries({ ...filters, continuationToken }).forEach(
                ([name, value]) => value && params.set(name, value),
            );
            return this.get<EmissionRecordsResponse>(
                params,
                "pipeline/EmissionsTimeline/GetEmissionRecords",
                abortSignal,
            );
        };

        let current = Math.min(page, tokens.length);
        let response = await fetchPage(tokens[current - 1]);
        while (current < page && response.continuationToken) {
            tokens[current++] = response.continuationToken;
            response = await fetchPage(response.continuationToken);
        }
        if (response.continuationToken)
            tokens[current] = response.continuationToken;

        return {
            items: current === page ? response.items : [],
            hasNextPage:
                current === page && Boolean(response.continuationToken),
        };
    }
}

export default EmissionsService;
